import type { MetaArtifact } from '@ev/shared'
import { create } from 'zustand'

/**
 * How healthy the published data is, as opposed to how healthy any one panel is.
 *
 * Three things can be wrong with a publish while the app itself works perfectly,
 * and a visitor can act on all three:
 *
 * - it is **stale** - the daily run has not landed, so what is on screen is
 *   older than it should be;
 * - it is **degraded** - the pipeline published without Open Charge Map, so
 *   every voltage is derived rather than declared;
 * - it is **partial** - one of the supplementary artifacts did not load, so
 *   availability or prices are missing while the map is fine.
 *
 * None of these is an error state for the map. The map keeps working; the notice
 * says what is missing. That is the difference between a site that degrades and
 * a site that goes blank.
 */

/**
 * The full set is published daily at 02:15 Europe/Warsaw
 * (`FULL_REFRESH_SCHEDULE`). Thirty hours is one missed run plus enough slack
 * that a late finish does not cry wolf.
 */
export const STALE_AFTER_HOURS = 30

const MS_PER_HOUR = 60 * 60 * 1000

export interface Staleness {
  readonly stale: boolean
  /** Whole hours since the publish. */
  readonly ageHours: number
  /** `meta.generated`, for formatting the date in the notice. */
  readonly generated: string
}

export const stalenessOf = (meta: MetaArtifact, now: number = Date.now()): Staleness => {
  const published = Date.parse(meta.generated)
  // An unparseable timestamp is a publish problem, but not one this notice can
  // describe honestly, so it is not called stale.
  if (Number.isNaN(published)) return { stale: false, ageHours: 0, generated: meta.generated }

  const ageHours = Math.max(0, Math.floor((now - published) / MS_PER_HOUR))
  return { stale: ageHours >= STALE_AFTER_HOURS, ageHours, generated: meta.generated }
}

/**
 * The supplementary artifacts, whose absence degrades the app rather than
 * breaking it. `stations.geojson` is deliberately not on this list: without it
 * there is no map, so it is a hard failure with its own error state.
 */
export type OptionalArtifact = 'status' | 'prices'

interface DataHealthState {
  /** Artifacts that failed to load at least once this session. */
  readonly unavailable: readonly OptionalArtifact[]
  markUnavailable(artifact: OptionalArtifact): void
  markAvailable(artifact: OptionalArtifact): void
}

/**
 * A separate store from the app store on purpose: the app store is defined as
 * "only what the URL holds", and whether `status.json` happened to 500 is not
 * part of a shareable view.
 */
export const useDataHealthStore = create<DataHealthState>((set, get) => ({
  unavailable: [],

  markUnavailable: (artifact) => {
    if (get().unavailable.includes(artifact)) return
    set({ unavailable: [...get().unavailable, artifact] })
  },

  markAvailable: (artifact) => {
    if (!get().unavailable.includes(artifact)) return
    set({ unavailable: get().unavailable.filter((entry) => entry !== artifact) })
  },
}))

export const selectUnavailable = (state: DataHealthState): readonly OptionalArtifact[] =>
  state.unavailable
